// Device tilt + shake. Tilting the phone applies a "gravity" force to the drifting field;
// shaking scatters the diatoms (which then collide and ring). iOS 13+ gates DeviceOrientation/
// DeviceMotion behind a permission prompt that must be requested from a user gesture (we use the
// Enter tap); Android/desktop auto-grant. All side-effect-free mapping lives in `tiltToGravity`.

/**
 * Pure: map orientation angles to a gravity vector (px/s^2).
 * `gamma` (left-right, [-90,90]) -> gx; `beta` (front-back, [-180,180]) -> gy. Each is normalized
 * over ~40° of tilt and clamped to [-1,1], then scaled by `gMax`. Level phone -> (0,0).
 */
export function tiltToGravity(beta: number, gamma: number, gMax: number): { gx: number; gy: number } {
  const clamp = (v: number) => (v < -1 ? -1 : v > 1 ? 1 : v);
  return { gx: clamp(gamma / 40) * gMax, gy: clamp(beta / 40) * gMax };
}

export interface DeviceTiltOpts {
  gMax?: number; // gravity magnitude at full tilt (px/s^2)
  shakeThreshold?: number; // |accel incl. gravity| (m/s^2) above which a shake fires (gravity ~9.8)
  shakeCooldownMs?: number; // min gap between shake events
}

export class DeviceTilt {
  private lastShake = 0;
  private orientHandler: ((e: DeviceOrientationEvent) => void) | null = null;
  private motionHandler: ((e: DeviceMotionEvent) => void) | null = null;

  constructor(
    private readonly onGravity: (gx: number, gy: number) => void,
    private readonly onShake: () => void,
    private readonly opts: DeviceTiltOpts = {},
  ) {}

  static supported(): boolean {
    return typeof window !== 'undefined' && ('DeviceOrientationEvent' in window || 'DeviceMotionEvent' in window);
  }

  /** Request iOS permission (no-op/auto-grant elsewhere). Call from inside a user gesture. */
  static async requestPermission(): Promise<boolean> {
    const ask = (name: 'DeviceOrientationEvent' | 'DeviceMotionEvent'): Promise<boolean> => {
      const Ctor = (window as unknown as Record<string, { requestPermission?: () => Promise<string> }>)[name];
      if (Ctor && typeof Ctor.requestPermission === 'function') {
        // Invoke requestPermission() synchronously here (caller is inside the gesture); await later.
        return Ctor.requestPermission()
          .then((r) => r === 'granted')
          .catch(() => false);
      }
      return Promise.resolve(true); // not gated (Android/desktop) -> treat as granted
    };
    // Kick BOTH prompts off before awaiting either, so the motion request (shake) is still inside the
    // user-activation window on iOS — awaiting orientation first would consume the gesture.
    const [orientation] = await Promise.all([ask('DeviceOrientationEvent'), ask('DeviceMotionEvent')]);
    return orientation; // gravity needs orientation; shake (motion) is best-effort
  }

  start(): void {
    const gMax = this.opts.gMax ?? 100;
    const thr = this.opts.shakeThreshold ?? 24;
    const cd = this.opts.shakeCooldownMs ?? 600;
    this.orientHandler = (e) => {
      const { gx, gy } = tiltToGravity(e.beta ?? 0, e.gamma ?? 0, gMax);
      this.onGravity(gx, gy);
    };
    this.motionHandler = (e) => {
      const a = e.accelerationIncludingGravity;
      if (!a) return;
      const mag = Math.hypot(a.x ?? 0, a.y ?? 0, a.z ?? 0);
      if (mag > thr) {
        const now = performance.now();
        if (now - this.lastShake > cd) {
          this.lastShake = now;
          this.onShake();
        }
      }
    };
    window.addEventListener('deviceorientation', this.orientHandler, { passive: true });
    window.addEventListener('devicemotion', this.motionHandler, { passive: true });
  }

  stop(): void {
    if (this.orientHandler) window.removeEventListener('deviceorientation', this.orientHandler);
    if (this.motionHandler) window.removeEventListener('devicemotion', this.motionHandler);
    this.orientHandler = null;
    this.motionHandler = null;
  }
}
